/**
 * Organizations Table Component
 *
 * Displays all organizations in a table format with subscription tier,
 * status, and last activity information.
 */

import { PlatformOrganization } from '../../api/platform';

interface OrganizationsTableProps {
  organizations: PlatformOrganization[];
  loading: boolean;
  onRowClick?: (org: PlatformOrganization) => void;
}

export default function OrganizationsTable({
  organizations,
  loading,
  onRowClick,
}: OrganizationsTableProps) {
  if (loading) {
    return <TableSkeleton />;
  }

  if (organizations.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
        <p className="text-gray-500">No organizations found</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Organization
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Tier
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Members
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Last Activity
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {organizations.map((org) => (
            <tr
              key={org.id}
              onClick={() => onRowClick?.(org)}
              className={onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''}
            >
              <td className="px-6 py-4 whitespace-nowrap">
                <div>
                  <div className="text-sm font-medium text-gray-900">{org.name}</div>
                  <div className="text-sm text-gray-500">{org.slug}</div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <TierBadge tier={org.subscriptionTier} />
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <StatusBadge isActive={org.isActive} />
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {org.memberCount}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {formatLastActivity(org.lastActivityAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// =============================================================================
// Sub-components
// =============================================================================

function TierBadge({ tier }: { tier: string }) {
  const colors: Record<string, string> = {
    FREE: 'bg-gray-100 text-gray-800',
    PRO: 'bg-blue-100 text-blue-800',
    ENTERPRISE: 'bg-purple-100 text-purple-800',
  };

  return (
    <span
      className={`inline-flex px-2.5 py-0.5 text-xs font-medium ${
        colors[tier] || colors.FREE
      }`}
    >
      {tier}
    </span>
  );
}

function StatusBadge({ isActive }: { isActive: boolean }) {
  return (
    <span
      className={`inline-flex px-2.5 py-0.5 text-xs font-medium ${
        isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
      }`}
    >
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );
}

function formatLastActivity(dateStr: string | null): string {
  if (!dateStr) {
    return 'No events';
  }

  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  } else {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  }
}

function TableSkeleton() {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Organization
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Tier
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Members
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Last Activity
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {[1, 2, 3, 4, 5].map((i) => (
            <tr key={i}>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                  <div className="h-3 bg-gray-100 rounded w-20"></div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="animate-pulse">
                  <div className="h-5 bg-gray-200 rounded w-16"></div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="animate-pulse">
                  <div className="h-5 bg-gray-200 rounded w-14"></div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-8"></div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-20"></div>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
